/* =======================================================================
   Global Variables and Element References
======================================================================== */

/**
 * Global counter to assign unique IDs to the "Show more" buttons.
 */
let showMoreBtnCounter = 1;

/**
 * Stores the discount brackets extracted from the discount table.
 */
let discountBrackets = [];

/**
 * Variable to track whether to include VAT
 */
let includeVAT = true;

/**
 * Retrieve the current market VAT rate.
 * If the input is "21", it will be converted to 0.21 by dividing by 100.
 * If the input is "21.5", it becomes 0.215, which is 21.5%.
 */
const vatRateInput = document.getElementById('current-market-vat-rate');
let vatRate = 0;

if (vatRateInput) {
  const rawVATValue = parseFloat(vatRateInput.value);
  if (!isNaN(rawVATValue)) {
    // Convert from "21" or "21.5" to "0.21" or "0.215"
    vatRate = rawVATValue / 100;
  }
}

/** 
 * Get the Market ID from the hidden input on the page
 */
const marketId_element = document.getElementById('store-market-id');
const marketId = marketId_element.value;

/** 
 * Get the ISO code from the hidden input on the page
 */
const iso_code_element = document.getElementById('store-iso-code');
const iso_code = iso_code_element ? iso_code_element.value : 'en'; // Default to 'en' if not found

/** 
 * Get the Product ID from the hidden input on the page
 */
const productId = document.querySelector('input[name="product-id"]').value;

/** 
 * Global array to store excluded variant IDs
 */
let excludedVariantIds = [];

/**
 * Global currency symbol, detected from original prices
 */
let currencySymbol = '';

/**
 * Indicates whether the currency comes before or after the price. Values: ‘before’ | ‘after’
 */
let currencySymbolPlacement = 'after';
let detectedDecimalSeparator  = '.';   // "."  or ","
let detectedThousandSeparator = ',';   // ","  or "."
let separatorsLocked = false;

/**
 * Declare original single unit and compare prices
 */
let originalSingleUnitPriceWithVAT = 0;
let originalComparePriceWithVAT = 0;

/**
 * Flag indicating whether the discount percentage column exists.
 * Assuming it always exists in this implementation.
 */
let discountPercentageColumnExists = true;

/**
 * Declare original print system iframe url src empty
 */
let printSystemIframeUrlSrc = "";

/**
 * DOM Element references used throughout the script.
 */
const personaliseProductIframe = document.getElementById('personalise-product-iframe');
const printSystemIframeUrlInput = document.getElementById('print-system-iframe-url');
const optionSelectors = document.querySelectorAll('.option-selector');
const discountTableElement = document.querySelector('.shopacado-discount-table');
const productSkuElement = document.querySelector('.product-sku');
const productMedia = document.getElementById('product-media');
const personaliseProductBtn = document.querySelector('#personalise-product-btn');
const quantityInput = document.querySelector('.qty-input__input');

/* =======================================================================
   Initialization Functions
======================================================================== */

/**
 * This code is executed once the DOM content has fully loaded. It ensures that
 * various initialization tasks are performed, including setting up prices, 
 * custom tabs, variant change handling, VAT toggle, and limiting variant options.
 */
document.addEventListener('DOMContentLoaded', async function() {

  // 1) Fetch excluded variants for that product in current market if product has more than 1 variant
  if(optionSelectors.length != 0){
    await fetchExcludedVariantIds();
  } 
  
  // 2) Initialize original product prices
  initializeOriginalPrices();
  
  // 3) Reorder measure-like fieldsets
  autoReorderMeasureFieldsets();

  // 4) Custom tabs
  initCustomTabs();

  // 5) Quantity input changes
  initQuantityInputChangeHandler();

  // 6) Toggle VAT inclusion
  initializeVATToggle();

  // 7) Limit variant options to show only 6, with a "Show more" button
  limitVariantOptions();

  // 8) Variant change handler
  initVariantChangeHandler();
});

/**
 * Initialize the original prices (price_current and price_was) with VAT
 * so we can compute exVAT if needed.
 */
function initializeOriginalPrices() {
  const priceCurrentElement = document.querySelector('.price__current');
  if (priceCurrentElement) {
    const priceText = priceCurrentElement.querySelector('.js-value').textContent.trim();

    // Detect the currency symbol by removing digits, commas, periods, and spaces
    const symbolMatch = priceText.replace(/[0-9.,\s]/g, '');
    currencySymbol = symbolMatch || '';

    // Determine where the currency symbol should be placed
    currencySymbolPlacement = determineCurrencySymbolPlacement(priceText, currencySymbol);

    // Get the base numeric price value
    originalSingleUnitPriceWithVAT = parseUnitPrice(priceText);
  } else {
    // Default to 0 if the price element is not found
    originalSingleUnitPriceWithVAT = 0;
    currencySymbol = '';
  }

  // Get the "was" price and extract its value
  const priceWasElement = document.querySelector('.price__was');
  if (priceWasElement) {
    const wasText = priceWasElement.textContent.trim();
    originalComparePriceWithVAT = parseUnitPrice(wasText);
  } else {
    originalComparePriceWithVAT = 0;
  }
}

/**
 * Function to fetch excluded variant IDs of the product by current market
 */
async function fetchExcludedVariantIds() {
  try {
    const response = await fetch('https://api-shopify.enveseur.com/shopify-product/excluded-market-variants/product/' + productId + '/market/' + marketId);
    excludedVariantIds = await response.json();
  } catch (error) {
    console.error("Error fetching excluded variant IDs:", error);
  }
}

/* =======================================================================
   Variant Handling Functions
======================================================================== */

/**
 * Retrieves the selected variant based on the selected options.
 * @param {NodeList} optionSelectors - The list of option selector elements.
 * @returns {Object|null} The selected variant object or null if not found.
 */
function getSelectedVariant(optionSelectors) {
  const productJsonElement = document.querySelector('variant-picker script[type="application/json"]');

  if (productJsonElement) {
    let productData = null;
    try {
      productData = JSON.parse(productJsonElement.textContent);
    } catch (e) {
      return null;
    }

    const selectedOptions = Array.from(optionSelectors).map((fieldset) => {
      const checkedInput = fieldset.querySelector('input:checked');
      return checkedInput ? checkedInput.value : null;
    });

    // If no options are selected, return the first available variant
    if (selectedOptions.includes(null)) {
      return productData.product.variants[0] || null;
    }

    // Find the variant that matches the selected options
    return productData.product.variants.find(variant => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });
  }else {
    // No productJsonElement found, likely a single product without multiple variants.
    // Retrieve the variant SKU from the span '.product-variant_sku__value'
    // Retrieve the variant ID from the hidden input '#product-variant_id__value'
    const skuElement = document.querySelector('.product-sku__value');
    const singleVariantSkuValue = skuElement.textContent;
    const singleVariantIdInput = document.getElementById('product-variant_id__value');
    if (singleVariantSkuValue && singleVariantIdInput) {
      const variantSku = singleVariantSkuValue;
      const variantId = singleVariantIdInput.value;
      
      // Return a minimal variant object containing the SKU and the ID.
      // If additional properties are needed, they can be included here.
      return {
        sku: variantSku,
        id: variantId
      };
    }
  }
  
  return null;
}

// If you have a single "personalise" button:
const personaliseButton = document.querySelector('#personalise-product-btn');
if (personaliseButton) {
  personaliseButton.addEventListener('click', () => {
    // Each time the user clicks, we set the iframe src
    // to the latest lazyIframeUrl (updated by variant changes).
    personaliseProductIframe.src = printSystemIframeUrlSrc;

    // Then open the modal
    window.location.hash = '#personalise-product-modal';
  });
}

const closeBtn = document.querySelector('#personalise-product-modal .close');
if (closeBtn) {
  closeBtn.addEventListener('click', () => {
    // Change the hash to close the modal (this will remove the :target state)
    window.location.hash = '';
  });
}

/**
 * Updates the iframe source with the provided SKU.
 * @param {string} sku - The SKU of the selected variant.
 */
function updatePersonaliseProductIframeSrc(sku) {
  if(printSystemIframeUrlInput){
    printSystemIframeUrlSrc = printSystemIframeUrlInput.value + sku;
  }
}

/**
 * Clears the iframe source.
 */
function clearPersonaliseProductIframeSrc() {
  if(personaliseProductIframe){
    personaliseProductIframe.src = '';
  } 
}

/**
 * Updates UI elements when a valid variant is selected.
 */
function updateUIForValidVariant() {
  // Remove blur effects and enable the "personalise" button
  productSkuElement?.classList.remove('blurred');
  productMedia?.classList.remove('blurred');
  discountTableElement?.classList.remove('blurred');
  personaliseProductBtn?.classList.remove('disabled');

  // Remove blur from the product metafields containers
  const metafieldsContainers = document.querySelectorAll('.product-metafields-container');
  metafieldsContainers.forEach(container => container.classList.remove('blurred'));
}

/**
 * Updates UI elements when no valid variant is selected.
 */
function updateUIForInvalidVariant() {
  // Apply blur effects and disable the "personalise" button
  productSkuElement?.classList.add('blurred');
  productMedia?.classList.add('blurred');
  discountTableElement?.classList.add('blurred');
  personaliseProductBtn?.classList.add('disabled');

  // Blur the product metafields containers
  const metafieldsContainers = document.querySelectorAll('.product-metafields-container');
  metafieldsContainers.forEach(container => container.classList.add('blurred'));

  // Clear discount brackets and remove any highlighting as the variant is invalid
  discountBrackets = [];
  highlightCurrentPriceBreak(null);
}

/**
 * Updates the price break highlight based on the current quantity.
 */
function updatePriceBreakHighlight() {
  initDiscountBracketsWhenTableIsReady(function() {
    if (quantityInput) {
      const quantity = parseInt(quantityInput.value);
      if (!isNaN(quantity)) {
        highlightCurrentPriceBreak(quantity);
      }
    }
  });
}

/**
 * Handles the variant change event.
 */
async function handleVariantChange() {
  // Retrieve the currently selected variant
  const variant = getSelectedVariant(optionSelectors);

  if (variant && variant.sku) {

    // If the variant is not available in the market, clear the iframe and update UI.
    if(optionSelectors.length != 0){
      if (excludedVariantIds.includes(String(variant.id))) {
        clearPersonaliseProductIframeSrc();
        updateUIForInvalidVariant();
  
        setTimeout(() => {
          // Disable the add-to-cart button and update its text.
          const addToCartButton = document.querySelector('.product-info__add-button button[name="add"]');
          if (addToCartButton) {
            addToCartButton.disabled = true;
            addToCartButton.textContent = 'Unavailable';
          }
          // Hide the element with class "price__default"
          const priceDefault = document.querySelector('.price__default');
          if (priceDefault) {
            priceDefault.setAttribute('hidden', '');
          }
          // Show the element with class "price__no-variant" by removing the hidden attribute.
          const priceNoVariant = document.querySelector('.price__no-variant');
          if (priceNoVariant) {
            priceNoVariant.removeAttribute('hidden');
          }
          // Add the class "blurred" to the discount table.
          if (discountTableElement) {
            discountTableElement.classList.add('blurred');
          }
        }, 50);
  
        return; // Stop further processing.
      }
    }
    
    // Update the iframe source to reflect the selected variant's SKU
    updatePersonaliseProductIframeSrc(variant.sku);

    // Update UI for a valid variant selection
    updateUIForValidVariant();

    // Reset the quantity input to 1 for the new variant
    if (quantityInput) {
      quantityInput.value = 1;
    }

    // Clear existing discount brackets to ensure fresh data for the new variant
    discountBrackets = [];

    // Use a short delay to ensure the DOM updates are complete before proceeding
    setTimeout(async () => {
      // Re-initialize original prices after switching to the new variant
      initializeOriginalPrices();

      // Wait until discount brackets are ready, then update table prices and the displayed price
      initDiscountBracketsWhenTableIsReady(async () => {
        updateDiscountTablePricesWithVAT();
        updatePrice(); // Recalculate prices after variant change
        
        // Highlight the discount bracket corresponding to quantity 1 (default quantity)
        if (quantityInput) {
          highlightCurrentPriceBreak(parseInt(quantityInput.value) || 1);
        }

        // Fetch metafields for the currently selected variant and display them in the containers
        if (variant.id) {
          // Fetch variant metafields if product has more than 1 variant
          if(optionSelectors.length != 0){
            const variantData = await fetchProductVariantMetafieldsApiCall(variant.id);
            if (variantData) {
              displayVariantMetafieldsForContainers(variantData);
            }
          }
        }
      });
    }, 50); // Adjust the delay if necessary to ensure all elements are ready
    
  } else {
    // If the variant is invalid (no SKU or not found), update UI to reflect an invalid selection
    clearPersonaliseProductIframeSrc();
    updateUIForInvalidVariant();
  }
}

/**
 * Initializes the variant change handler by adding event listeners.
 */
function initVariantChangeHandler() {
  // Add event listeners to variant inputs
  optionSelectors.forEach(function(fieldset) {
    const inputs = fieldset.querySelectorAll('input');
    inputs.forEach(function(input) {
      input.addEventListener('change', handleVariantChange);
    });
  });

  // Initialize on page load
  handleVariantChange();
}

/* =======================================================================
   Quantity Handling Functions
======================================================================== */

/**
 * Initializes the quantity input change handler.
 */
function initQuantityInputChangeHandler() {
  if (!quantityInput) {
    return;
  }

  // Initialize discount brackets when the table is ready
  initDiscountBracketsWhenTableIsReady(function() {
    const initialQuantity = parseInt(quantityInput.value);
    if (!isNaN(initialQuantity)) {
      highlightCurrentPriceBreak(initialQuantity);
    }
  });

  // Add event listener to quantity input
  quantityInput.addEventListener('input', function() {
    const quantity = parseInt(quantityInput.value);

    if (isNaN(quantity)) {
      highlightCurrentPriceBreak(null);
    } else {
      highlightCurrentPriceBreak(quantity);
    }

    // After highlighting the correct bracket row, update the main price
    updatePrice();
  });
}

/* =======================================================================
   Dispatch countdown widget
======================================================================== */

(function() {
  const widget = document.getElementById('custom-dispatch-countdown-widget');
  if (!widget) return;

  const today = new Date();
  const dow   = today.getDay();
  const mm    = String(today.getMonth() + 1).padStart(2, '0');
  const dd    = String(today.getDate()).padStart(2, '0');
  const todayKey = `${mm}-${dd}`;

  // 1) Weekend? hide.
  if (dow === 0 || dow === 6) {
    widget.style.display = 'none';
    return;
  }

  // 2) Spanish national bank holidays (MM-DD).
  const holidays = [
    '01-01', // New Year's Day
    '01-06', // Epiphany
    '05-01', // Labor Day
    '08-15', // Assumption
    '10-12', // National Day
    '11-01', // All Saints
    '12-06', // Constitution Day
    '12-08', // Immaculate Conception
    '12-25'  // Christmas
  ];
  if (holidays.includes(todayKey)) {
    widget.style.display = 'none';
    return;
  }
  
  const hrsSpan  = document.getElementById('custom-dispatch-countdown-hours-span');
  const minsSpan = document.getElementById('custom-dispatch-countdown-minutes-span');
  const secsSpan = document.getElementById('custom-dispatch-countdown-seconds-span');

  let interval;
  
  function updateTimer() {
    const nowUTC = Date.now();

    // 15:00 Madrid = 13:00 UTC
    const now     = new Date(nowUTC);
    const y       = now.getUTCFullYear();
    const m       = now.getUTCMonth();
    const d       = now.getUTCDate();
    const cutoffUTCms = Date.UTC(y, m, d, 13, 0, 0);

    let diff = cutoffUTCms - nowUTC;
    if (diff <= 0) {
      widget.style.display = 'none';
      clearInterval(interval);
      return;
    }

    const hrs  = Math.floor(diff / 3_600_000);
    diff %= 3_600_000;
    const mins = Math.floor(diff / 60_000);
    diff %= 60_000;
    const secs = Math.floor(diff / 1000);

    hrsSpan.textContent  = String(hrs).padStart(2, '0');
    minsSpan.textContent = String(mins).padStart(2, '0');
    secsSpan.textContent = String(secs).padStart(2, '0');
  }

  updateTimer();
  interval = setInterval(updateTimer, 1000);
})();

/* =======================================================================
   Reorder Measure Options Functions
======================================================================== */

/**
 * Parse strings like:
 *  "162 x 229 mm", "110×220", "82 x 113 mm (C7)", "110,5 x 220,3"
 * Returns an object with several numeric helpers or null if it can't parse.
 */
function parseWidthHeight(text = '') {
  const rx = /(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i;
  const m = rx.exec(text);
  if (!m) return null;

  const w = parseFloat(m[1].replace(',', '.'));
  const h = parseFloat(m[2].replace(',', '.'));

  return {
    width:   w,
    height:  h,
    first:   w,                 // keep original order
    second:  h,
    minSide: Math.min(w, h),
    maxSide: Math.max(w, h),
    area:    w * h
  };
}

/**
 * Reorders measure-like fieldsets automatically by checking if the radio inputs
 * look like "width x height".
 *
 * Logic:
 * 1. Find all fieldsets.option-selector (excluding quick-add drawer).
 * 2. For each, gather radio inputs and parse WxH from input value or label text.
 * 3. If >= half parse, sort parsed first by:
 *      first number  -> second number -> label text
 *    (Change to .area if you prefer total area instead)
 * 4. Re-append inputs + labels in that order, keeping visibility limits intact.
 */
function autoReorderMeasureFieldsets() {
  const fieldsets = document.querySelectorAll('fieldset.option-selector:not(.quick-add-drawer fieldset.option-selector)');

  fieldsets.forEach((fieldset) => {
    const container = fieldset.querySelector('.option-selector__btns');
    if (!container) return;

    const radioInputs = container.querySelectorAll('input[type="radio"].opt-btn');
    if (!radioInputs.length) return;

    let parseCount = 0;

    const items = Array.from(radioInputs).map((input) => {
      const label = container.querySelector(`label[for="${input.id}"]`);
      const labelText = label?.textContent.trim() || '';
      const parsed = parseWidthHeight(input.value) || parseWidthHeight(labelText);
      if (parsed) parseCount++;
      return {
        input,
        label,
        text: labelText || input.value,
        parsed
      };
    });

    // Skip if not mostly measures
    if (parseCount < items.length / 2) return;

    // Sort parsed first, then by first -> second numbers, else by text
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

    items.sort((a, b) => {
      const pa = a.parsed, pb = b.parsed;

      if (pa && !pb) return -1;
      if (!pa && pb) return 1;

      if (pa && pb) {
        if (pa.first !== pb.first)   return pa.first - pb.first;
        if (pa.second !== pb.second) return pa.second - pb.second;
      }

      return collator.compare(a.text, b.text);
    });

    // Re-append keeping input->label sequence
    const frag = document.createDocumentFragment();
    items.forEach(({ input, label }) => {
      if (input) frag.appendChild(input);
      if (label) frag.appendChild(label);
    });
    container.appendChild(frag);
  });
}

/* =======================================================================
   Discount Table Functions
======================================================================== */

/**
 * Wait until a discount table (.shopacado-discount-table) exists in the DOM,
 * then build the in‑memory `discountBrackets` array and finally run the
 * optional callback.
 *
 * • If the table is already in the HTML when the function is called,
 *   it populates `discountBrackets` immediately.  
 * • Otherwise it attaches a MutationObserver, runs the same routine as
 *   soon as the table is injected, and then disconnects the observer.
 *
 * @param {Function} [callback] code to execute after brackets are ready
 */
function initDiscountBracketsWhenTableIsReady(callback) {
  // Find the discount table
  const table = document.querySelector('.shopacado-discount-table');
  if (table) {
    // If the table is already available, initialize brackets immediately
    replaceDiscountTableHeaders();
    getDiscountBrackets();
    initRowClickToSetQuantity();
    callback?.();
  } else {
    // Use a MutationObserver to detect when the table is added to the DOM
    const observer = new MutationObserver(() => {
      const table = document.querySelector('.shopacado-discount-table');
      if (table) {
        observer.disconnect(); // Stop observing once the table is found
        replaceDiscountTableHeaders();
        getDiscountBrackets();
        initRowClickToSetQuantity();
        callback?.();
      }
    });

    // Observe changes in the DOM
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

/**
 * Read the two hidden inputs and replace the <th> texts
 * in the .shopacado-discount-table header.
 */
function replaceDiscountTableHeaders() {
  // 1. Grab the translated labels from the hidden inputs
  const qtyInput       = document.getElementById('product-quantity-discount-table_quantity_label');
  const priceInput     = document.getElementById('product-quantity-discount-table_rice-per-unit_label');
  const qtyLabel       = qtyInput ? qtyInput.value.trim()   : 'Quantity';
  const priceLabel     = priceInput ? priceInput.value.trim() : 'Price per unit';

  // 2. Find the two <th> cells in the table header
  const headerThs = document.querySelectorAll('.shopacado-discount-table thead th');
  if (headerThs.length >= 2) {
    headerThs[0].textContent = qtyLabel;
    headerThs[1].textContent = priceLabel;
  }
}


/**
 * Scan every row of <table.shopacado-discount-table> and build `discountBrackets`:
 *   [{ min, max, originalFinalPriceWithVAT, rowElement }, … ]
 *
 * Column assumptions (after the “hide discount column” tweak):
 *   0 → Quantity range ("1 - 9", "10000+")  
 *   1 → Discount %      (hidden but still in the DOM)  
 *   2 → Price per unit  (<span.money>)
 *
 * Uses `parseUnitPrice` so prices are read with the correct decimal sign.
 */
function getDiscountBrackets() {
  // Clear existing discount brackets
  discountBrackets = [];

  // Find the discount table in the DOM
  const table = document.querySelector('.shopacado-discount-table');
  if (!table) {
    return;
  }

  // Iterate through table rows to extract discount brackets
  const rows = table.querySelectorAll('tbody tr');
  rows.forEach((row) => {
    const cells = row.querySelectorAll('td');
    if (cells.length < 2) {
      return;
    }

    // Extract data from table cells
    const quantityRangeText = cells[0].textContent.trim();
    const unitPriceText = cells[1].querySelector('.money')?.textContent.trim() || "0";

    // Parse the extracted data into structured information
    const quantityRange = parseQuantityRange(quantityRangeText);
    const originalFinalPriceWithVAT = parseUnitPrice(unitPriceText);

    // Add the parsed bracket to the array
    discountBrackets.push({
      min: quantityRange.min,
      max: quantityRange.max,
      originalFinalPriceWithVAT: originalFinalPriceWithVAT,
      rowElement: row,
    });
  });

  // Ensure brackets are sorted by quantity range
  discountBrackets.sort((a, b) => a.min - b.min);
}

/**
 * Return the bracket that applies to a given quantity, or null if none fits.
 *
 * Bracket structure: { min, max, … }.  
 * A bracket with `max === Infinity` covers “10000+” style ranges.
 *
 * @param  {number} quantity
 * @return {Object|null}
 */
function getDiscountBracket(quantity) {
  return discountBrackets.find(bracket => {
    return quantity >= bracket.min && (quantity <= bracket.max || bracket.max === Infinity);
  }) || null;
}

/**
 * Visually highlight the row whose bracket matches the chosen quantity.
 *
 * Adds / removes the CSS class `.current-price-break`.
 *
 * @param {number|null} quantity current quantity (null → clear highlight)
 */
function highlightCurrentPriceBreak(quantity) {
  const tableRows = document.querySelectorAll('.shopacado-discount-table tbody tr');
  tableRows.forEach(row => {
    row.classList.remove('current-price-break');
  });

  if (quantity === null || quantity <= 0 || discountBrackets.length === 0) {
    return;
  }

  const currentBracket = getDiscountBracket(quantity);

  if (currentBracket) {
    currentBracket.rowElement.classList.add('current-price-break');
  }
}

/**
 * Convert a quantity‑range string into numeric bounds.
 *
 * Accepts forms like:
 *   "5 - 10"   → { min:5,   max:10 }  
 *   "10000+"   → { min:10000, max:Infinity }  
 *   "12"       → { min:12,  max:12 }
 *
 * @param  {string} rangeText
 * @return {{min:number, max:number}}
 */
function parseQuantityRange(rangeText) {
  let min = 0, max = Infinity;

  if (rangeText.includes('+')) {
    min = parseInt(rangeText.replace('+', '').trim());
  } else if (rangeText.includes('-')) {
    const [minText, maxText] = rangeText.split('-').map(s => s.trim());
    min = parseInt(minText);
    max = parseInt(maxText);
  } else {
    min = parseInt(rangeText.trim());
    max = min;
  }

  return { min, max };
}

/**
 * After `discountBrackets` has been populated, make each row clickable so
 * that clicking it sets the quantity input to the bracket’s minimum.
 * Runs once; extra safeties prevent duplicate listeners.
 */
function initRowClickToSetQuantity() {
  if (!discountBrackets.length || !quantityInput) return;

  discountBrackets.forEach(bracket => {
    const row = bracket.rowElement;
    if (!row || row.dataset.listenerAdded) return;   // already wired

    row.style.cursor = 'pointer';                   // visual hint

    row.addEventListener('click', () => {
      // 1 – set quantity to the bracket’s minimum
      quantityInput.value = bracket.min;

      // 2 – let the rest of the code react (highlight + price update)
      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    });

    row.dataset.listenerAdded = 'true';
  });
}

/* =======================================================================
   Custom Tabs Functions
======================================================================== */

/**
 * Initializes custom tab functionalities.
 */
function initCustomTabs() {
  const specButton = document.querySelector('.product-specifications-btn');
  if (specButton) {
    specButton.addEventListener('click', () => {
      const tab = document.querySelector('#tab-4');
      if (tab) {
        tab.click();
        smoothScrollTo(tab, 1500);
      }
    });
  }
}

/* =======================================================================
   Limit Variant Options Functions
======================================================================== */

/**
 * Limits the displayed variant options to a specific number and adds "Show more"/"Show less" functionality.
 * Does NOT require .product-variant__item wrappers. Instead, it treats each label as one variant item.
 */
function limitVariantOptions() {
  const VISIBLE_ITEM_COUNT = 10;

  // Retrieve localized strings from hidden inputs
  const showMoreInput = document.getElementById('product-option-selector-show-more-label');
  const showLessInput = document.getElementById('product-option-selector-show-less-label');
  const showMoreText = showMoreInput?.value || 'Show more';
  const showLessText = showLessInput?.value || 'Show less';

  // Instead of .product-variant__container, select each fieldset
  const fieldsets = document
  .querySelector('.product-main')
  ?.querySelectorAll('fieldset.option-selector');

  fieldsets.forEach(fieldset => {
    // Gather all <label class="opt-label"> inside .option-selector__btns
    const variantLabels = Array.from(
      fieldset.querySelectorAll('.option-selector__btns label.opt-label')
    );

    // Find which labels are "enabled" by checking the matching input
    const enabledVariantLabels = variantLabels.filter(label => {
      const inputId = label.getAttribute('for');
      if (!inputId) return false;

      const input = fieldset.querySelector(`#${inputId}`);
      if (!input) return false;

      const isItemDisabled = input.classList.contains('disabled');
      const isInputDisabled = input.disabled;
      return !isItemDisabled && !isInputDisabled;
    });

    if (enabledVariantLabels.length > VISIBLE_ITEM_COUNT) {
      let enabledIndex = 0;

      // Show/hide each label
      variantLabels.forEach(label => {
        if (enabledVariantLabels.includes(label)) {
          if (enabledIndex < VISIBLE_ITEM_COUNT) {
            label.classList.remove('custom-hidden');
          } else {
            label.classList.add('custom-hidden');
          }
          enabledIndex++;
        } else {
          // If "disabled," we usually keep it visible but unclickable
          label.classList.remove('custom-hidden');
        }
      });

      // Look up the .show-more-btn-container
      let showMoreBtnContainer = fieldset.nextElementSibling;
      if (!showMoreBtnContainer?.classList.contains('show-more-btn-container')) {
        showMoreBtnContainer = fieldset.parentElement.querySelector('.show-more-btn-container');
      }
      if (!showMoreBtnContainer) return; // no container => can't do show more/less

      let showMoreBtn = showMoreBtnContainer.querySelector('.show-more-btn');
      if (!showMoreBtn) {
        // Create the button if it doesn't exist
        showMoreBtn = document.createElement('button');
        showMoreBtn.classList.add('show-more-btn', 'text-decoration--underline');
        showMoreBtn.textContent = showMoreText;  // Use localized “Show more” text

        // unique ID for localStorage
        showMoreBtn.id = 'show-more-btn-' + showMoreBtnCounter;
        showMoreBtnCounter++;

        showMoreBtnContainer.appendChild(showMoreBtn);
      } else {
        // ensure visible
        showMoreBtn.classList.remove('hidden');
      }

      // Add toggle logic once
      if (!showMoreBtn.dataset.listenerAdded) {
        showMoreBtn.addEventListener('click', () => {
          const isExpanded = showMoreBtn.textContent === showLessText;

          if (isExpanded) {
            // Hide items beyond first X
            let idx = 0;
            enabledVariantLabels.forEach(label => {
              if (idx >= VISIBLE_ITEM_COUNT) {
                label.classList.add('custom-hidden');
              }
              idx++;
            });
            showMoreBtn.textContent = showMoreText;
            setShowMoreState(showMoreBtn.id, false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            // Show all
            enabledVariantLabels.forEach(label => label.classList.remove('custom-hidden'));
            showMoreBtn.textContent = showLessText;
            setShowMoreState(showMoreBtn.id, true);
          }
        });
        showMoreBtn.dataset.listenerAdded = 'true';
      }

      // Apply saved expand/collapse state
      const isExpanded = getShowMoreState(showMoreBtn.id);
      if (isExpanded) {
        enabledVariantLabels.forEach(label => label.classList.remove('custom-hidden'));
        showMoreBtn.textContent = showLessText;
      } else {
        let idx = 0;
        enabledVariantLabels.forEach(label => {
          if (idx >= VISIBLE_ITEM_COUNT) {
            label.classList.add('custom-hidden');
          }
          idx++;
        });
        showMoreBtn.textContent = showMoreText;
      }

    } else {
      // fewer or equal => show all
      variantLabels.forEach(label => label.classList.remove('custom-hidden'));

      // hide any existing "Show more" button
      let showMoreBtnContainer = fieldset.nextElementSibling;
      if (!showMoreBtnContainer?.classList.contains('show-more-btn-container')) {
        showMoreBtnContainer = fieldset.parentElement.querySelector('.show-more-btn-container');
      }
      if (showMoreBtnContainer) {
        const showMoreBtn = showMoreBtnContainer.querySelector('.show-more-btn');
        if (showMoreBtn) {
          showMoreBtn.classList.add('hidden');
        }
      }
    }
  });
}

/**
 * Saves the expanded/collapsed state of a "Show more" button.
 */
function setShowMoreState(buttonId, isExpanded) {
  localStorage.setItem(buttonId, isExpanded ? 'true' : 'false');
}

/**
 * Retrieves the expanded/collapsed state of a "Show more" button.
 */
function getShowMoreState(buttonId) {
  const state = localStorage.getItem(buttonId);
  if (state !== null) {
    return state === 'true';
  }
  return false;
}

/* =======================================================================
   Toggle VAT Functions
======================================================================== */

/**
 * On load, we have everything with VAT by default.
 * If includeVAT=false from cookie, we must convert all (table and price_current) to exVAT by dividing by (1+vatRate).
 */
function initializeVATToggle() {
  const vatCheckbox = document.getElementById('show-vat-checkbox');
  if (!vatCheckbox) {
    return;
  }

  const vatCookie = getCookie('includeVAT');
  if (vatCookie !== null) {
    includeVAT = (vatCookie === 'true');
  } else {
    includeVAT = true;
    setCookie('includeVAT', true, 30);
  }

  vatCheckbox.checked = includeVAT;

  vatCheckbox.addEventListener('change', function() {
    includeVAT = vatCheckbox.checked;
    setCookie('includeVAT', includeVAT, 30);

    updateDiscountTablePricesWithVAT();
    updatePrice();
  });
}

/* =======================================================================
   Price Update Functions
======================================================================== */

/**
 * Updates each “Price per unit” cell in the Shopacado discount table:
 *
 * – Reads the `data-shopacado-discount-val` from the 2nd column (`<td>`)
 * – Applies that discount to `originalSingleUnitPriceWithVAT`
 * – Adds VAT if `includeVAT` is true
 * – Renders the result with exactly 4 decimal places
 * – Writes the formatted price into a `<span class="money">…</span>` directly in the same cell
 * – Then calls `rewriteQtyPricesToMultipleDecimals()` to visually grey-out the last 2 decimals
 */
function updateDiscountTablePricesWithVAT() {
  const table = document.querySelector('.shopacado-discount-table');
  if (!table) return;

  const rows = table.querySelectorAll('tbody tr');
  rows.forEach(row => {
    const cells = row.querySelectorAll('td');
    if (cells.length < 2) return;

    const discountAttr = cells[1].getAttribute('data-shopacado-discount-val');
    const discountValue = parseFloat(discountAttr) || 0;

    let finalUnitPrice = originalSingleUnitPriceWithVAT * (1 - discountValue / 100);

    // 1) Add VAT if enabled
    if (includeVAT) {
      finalUnitPrice = finalUnitPrice * (1 + vatRate);
    }

    // 2) When VAT is DISABLED, inspect 3rd decimal and round/truncate accordingly
    if (!includeVAT) {
      // extract the thousandths digit
      const thousandth = Math.floor(finalUnitPrice * 1000) % 10;
      if (thousandth >= 5) {
        // round up to next cent
        finalUnitPrice = Math.ceil(finalUnitPrice * 100) / 100;
      } else {
        // truncate (round down) to two decimals
        finalUnitPrice = Math.floor(finalUnitPrice * 100) / 100;
      }
    }

    // always show 4 decimals; last two will be zeros when VAT is off
    const numericOnly = finalUnitPrice.toFixed(4);
    
    cells[1].innerHTML = `<span class="money">${numericOnly}</span>`;
  });

  rewriteQtyPricesToMultipleDecimals();
}

/**
 * Enhances the visual formatting of all <span class="money">…</span> in the table:
 *
 * – Ensures every number has 4 decimal places (assumed from `.toFixed(4)` previously)
 * – Applies the correct `detectedDecimalSeparator` (either "," or ".")
 * – Wraps the last 2 decimal digits in a `<span style="color:#666">` to make them subtle
 * – Adds the currency symbol either before or after, depending on `currencySymbolPlacement`
 */
function rewriteQtyPricesToMultipleDecimals() {
  const table = document.querySelector('.shopacado-discount-table');
  if (!table) return;

  const moneyElements = table.querySelectorAll('tbody tr td .money');
  if (!moneyElements.length) return;

  const decimalSeparator = detectedDecimalSeparator;

  moneyElements.forEach(moneyEl => {
    let numericText = moneyEl.textContent.trim(); // Example: "0.3700"

    let [intPart, fracPart] = numericText.split('.');
    if (!intPart || !fracPart) return;

    fracPart = fracPart.padEnd(4, '0');

    const mainFraction = fracPart.slice(0, 2);
    const lastTwo = fracPart.slice(2);

    const numberWithSeparator = intPart + decimalSeparator + mainFraction + `<span style="color:#888">${lastTwo}</span>`;

    const finalText = currencySymbolPlacement === 'before'
      ? `${currencySymbol}&nbsp;${numberWithSeparator}`
      : `${numberWithSeparator}${currencySymbol}`;

    moneyEl.innerHTML = finalText;
  });
}

/**
 * Recalculate the main product price:
 *   – base unit price → apply the active discount bracket  
 *   – market-specific cents handling (India: always round up; HK: always truncate; others: round nearest)  
 *   – add/remove VAT per `includeVAT`  
 *   – multiply by chosen quantity  
 *   – write the formatted total to `.price__current`  
 *     (and update the “was” price, if present).
 */

// Market identifiers
const INDIA_ID = '31056592960';
const HK_ID    = '31027789888';

function updatePrice() {
  // --- Helpers for cent-level math ---
  const EPS = Number.EPSILON;
  function roundToCents(num) {
    return Math.round((num + EPS) * 100) / 100;
  }
  function ceilToCents(num) {
    return Math.ceil((num + EPS) * 100) / 100;
  }
  function truncToCents(num) {
    return Math.floor((num + EPS) * 100) / 100;
  }

  // 1) Locate the element where we'll display the price
  const priceCurrent = document.querySelector('.price__current');
  if (!priceCurrent) return;

  // 2) Read and validate the desired quantity
  const quantity = parseInt(quantityInput?.value || "1", 10);
  if (isNaN(quantity) || quantity < 1) return;

  // 3) Determine which discount bracket applies
  const bracket = getDiscountBracket(quantity);

  // 4) Pull in the base price (without VAT) and discount percentage
  let basePrice = originalSingleUnitPriceWithVAT;
  let discountPercentage = 0;
  if (bracket) {
    const cell = bracket.rowElement.querySelector('td[data-shopacado-discount-val]');
    discountPercentage = parseFloat(cell.getAttribute('data-shopacado-discount-val') || "0");
  }

  // 5) Compute the raw discount factor (e.g. 0.855 for 14.5% off),
  //    then truncate that factor to two decimals (e.g. 0.85)
  const rawFactor      = 1 - discountPercentage / 100;
  const discountFactor = Math.floor(rawFactor * 100) / 100;
  let unitPrice        = basePrice * discountFactor;

  // 6) Market-specific cents handling:
  //    India → always round up; Hong Kong → always truncate; others → round to nearest
  const mId = String(marketId);
  if (mId === INDIA_ID) {
    unitPrice = ceilToCents(unitPrice);
  } else if (mId === HK_ID) {
    unitPrice = truncToCents(unitPrice);
  } else {
    unitPrice = roundToCents(unitPrice);
  }

  // 7) VAT adjustment: if VAT is enabled, apply rate then truncate to cents
  if (includeVAT) {
    unitPrice = unitPrice * (1 + vatRate);
    unitPrice = truncToCents(unitPrice);
  }

  // 8) Calculate total = unitPrice * quantity, then render
  const totalPrice = unitPrice * quantity;
  priceCurrent.querySelector('.js-value').textContent = formatPrice(totalPrice, 2);

  // 9) If a “was” price exists, update it similarly
  const priceWas = document.querySelector('.price__was');
  if (priceWas && originalComparePriceWithVAT > 0) {
    let compareUnit = originalComparePriceWithVAT;
    if (includeVAT) compareUnit *= (1 + vatRate);
    compareUnit = truncToCents(compareUnit);
    const compareTotal = compareUnit * quantity;
    priceWas.querySelector('.js-value').textContent = formatPrice(compareTotal, 2);
  }
}

/* =======================================================================
   Metafields Functions
======================================================================== */

/**
 * Fetches the variant metafields from the backend API using the given iso_code.
 * @param {string} variantId - The ID of the variant to fetch metafields for.
 */
function fetchProductVariantMetafieldsApiCall(variantId) {
  return fetch('https://api-shopify.enveseur.com/product/variant/' + variantId + '/lang/' + iso_code)
  .then(response => response.json())
  .catch(error => {
      console.error('Error fetching variant metafields:', error);
  });
}

/**
 * Populates the metafield values in all <td> elements with matching class names.
 * If a metafield is missing or its value is "No value", hides the corresponding <tr>.
 *
 * @param {Object} variant - The variant data containing metafields.
 */
function displayVariantMetafieldsForContainers(variant) {
  // Iterate through the metafields in the variant data
  if (variant.metafields) {
    for (const key in variant.metafields) {
      if (variant.metafields.hasOwnProperty(key)) {
        const metafield = variant.metafields[key];

        // Select all <td> elements with the corresponding class
        const tdElements = document.querySelectorAll(`.product-property-value-${key}`);

        // Check if the metafield has a valid value
        const hasValue = metafield.value && metafield.value !== 'No value' && metafield.value !== 'N';

        if (tdElements.length > 0) {
          tdElements.forEach(tdElement => {
            const parentRow = tdElement.closest('tr');
            if (hasValue) {
              // Populate the element with the metafield value
              var metafield_value = metafield.value;
              if(metafield_value == "Y"){
                metafield_value = '<i class="fa-solid fa-check"></i>';
              }
              
              tdElement.innerHTML = metafield_value;
              
              if (parentRow) {
                parentRow.classList.remove('custom-hidden');
              }
            } else if (parentRow) {
              // Hide the corresponding <tr> if no value
              parentRow.classList.add('custom-hidden');
            }
          });
        }
      }
    }
  }

  // Hide any remaining rows with no matching metafield
  const allRows = document.querySelectorAll('.product-metafields-container tr');
  allRows.forEach(row => {
    const key = row.className.match(/product-property-([^\s]+)/)?.[1];
    if (key && !variant.metafields.hasOwnProperty(key)) {
      row.classList.add('custom-hidden');
    }
  });
}

/**
 * Formats the metafield key to be more readable if no custom title is found.
 * Replaces underscores with spaces and capitalizes words.
 * @param {string} key - The metafield key to format.
 * @returns {string} The formatted key.
 */
function formatMetafieldKey(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/* =======================================================================
   Cookie Helper Functions
======================================================================== */

/**
 * Sets a cookie with a given name, value, and expiration in days.
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value to store in the cookie.
 * @param {number} days - The number of days until the cookie expires.
 */
function setCookie(name, value, days) {
  var expires = "";
  if (days) {
      var date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
}

/**
 * Gets the value of a cookie by name.
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string|null} The value of the cookie or null if not found.
 */
function getCookie(name) {
  var nameEQ = name + "=";
  var ca = document.cookie.split(';');
  for (var i = 0; i < ca.length; i++) {
      var c = ca[i];
      while (c.charAt(0) == ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

/* =======================================================================
   Price Parsing/Formatting Helper Functions
======================================================================== */

/**
 * Parse the first price‑like token in `priceText` and return its numeric value.
 * While doing so, detect which character is the decimal separator and which
 * (if any) is the thousands separator, and store them in the globals
 *   detectedDecimalSeparator   // "."  or ","
 *   detectedThousandSeparator  // ","  or "."
 *
 * Handles examples such as:
 *   "Dhs 1.04"      → 1.04    (decimal "."  thousands ",")
 *   "0,21€"         → 0.21    (decimal ","  thousands ".")
 *   "€ 1.234,56"    → 1234.56 (decimal ","  thousands ".")
 *   "$1,234.56"     → 1234.56 (decimal "."  thousands ",")
 *
 * @param  {string} priceText  Full string that contains a price + currency.
 * @return {number}            Float value of the price (0 if no digits found).
 */
function parseUnitPrice(priceText) {
  if (!priceText) return 0;

  // -- common routine to grab the numeric token ------------------------
  const numberToken = priceText.match(/\d+(?:[.,]\d+)*/);
  if (!numberToken) return 0;
  let token = numberToken[0];          // e.g. "1.234,56"  "0,21"  "1.04"

  // -- first call: detect separators and lock them ---------------------
  if (!separatorsLocked) {
    const lastDot   = token.lastIndexOf('.');
    const lastComma = token.lastIndexOf(',');

    if (lastDot !== -1 && lastComma !== -1) {
      if (lastDot > lastComma) {                     // dot is decimal
        detectedDecimalSeparator  = '.';
        detectedThousandSeparator = ',';
        token = token.replace(/,/g, '');
      } else {                                       // comma is decimal
        detectedDecimalSeparator  = ',';
        detectedThousandSeparator = '.';
        token = token.replace(/\./g, '').replace(',', '.');
      }
    } else if (lastComma !== -1) {                   // only comma present
      detectedDecimalSeparator  = ',';
      detectedThousandSeparator = '.';
      token = token.replace(',', '.');
    } else {                                        // only dot present
      detectedDecimalSeparator  = '.';
      detectedThousandSeparator = ',';
      // token already OK
    }

    separatorsLocked = true;                        // freeze settings
  }
  // -- subsequent calls: use the stored separators ---------------------
  else {
    // remove thousands, normalise decimal to dot
    const thouRx = new RegExp('\\' + detectedThousandSeparator, 'g');
    token = token.replace(thouRx, '')
                 .replace(detectedDecimalSeparator, '.');
  }

  const value = parseFloat(token);
  return isNaN(value) ? 0 : value;
}

/**
 * Extract a “‑ X %” discount from a cell’s text.
 *
 * @param  {string} cellText  e.g. "- 10%", "-15%", "- 5.5%"
 * @return {number}           numeric value (10, 15, 5.5 …) or 0 if not found
 */
function parseDiscountPercentage(cellText) {
  const match = cellText.match(/-\s*(\d+(?:\.\d+)?)%/);
  return match ? parseFloat(match[1]) || 0 : 0;
}

/**
 * Format a number using the separators detected in `parseUnitPrice` and the
 * currency layout detected in `initializeOriginalPrices`.
 *
 * @param  {number} price      value to format
 * @param  {number} [decimals] number of decimal places (default 2)
 * @return {string}            e.g. "Dhs 1.0400", "12,34€", "£ 1,234.56"
 */
function formatPrice(price, decimals = 2) {
  // 1 – fixed‑point with dot decimal
  let num = price.toFixed(decimals);
  let [intPart, fracPart] = num.split('.');

  // 2 – add thousands separators
  intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, detectedThousandSeparator);

  // 3 – join with the detected decimal separator
  const numberPart = decimals
    ? intPart + detectedDecimalSeparator + fracPart
    : intPart;

  // 4 – place the currency symbol
  return currencySymbolPlacement === 'before'
    ? currencySymbol + ' ' + numberPart
    : numberPart + currencySymbol;
}

/**
 * Decide whether a currency symbol appears before or after the digits in
 * a raw price string (e.g. "£ 12.34" vs "12,34€").
 *
 * @param  {string} priceText full text that contains the price
 * @param  {string} symbol    currency symbol (already extracted)
 * @return {'before'|'after'} placement of the symbol
 */
function determineCurrencySymbolPlacement(priceText, symbol) {
  const firstDigitIdx  = priceText.search(/\d/);
  const firstSymbolIdx = priceText.indexOf(symbol);
  return (firstSymbolIdx !== -1 && firstSymbolIdx < firstDigitIdx) ? 'before' : 'after';
}

/* =======================================================================
   Utility Functions
======================================================================== */

/**
 * Smoothly scrolls to a target element over a specified duration.
 * @param {Element} target - The element to scroll to.
 * @param {number} duration - The duration of the scroll in milliseconds.
 */
function smoothScrollTo(target, duration) {
  const start = window.pageYOffset;
  const stickyMenu = document.querySelector('.header');
  const stickyMenuHeight = stickyMenu ? stickyMenu.offsetHeight : 0;
  const targetPosition = target.getBoundingClientRect().top + start - stickyMenuHeight;
  const distance = targetPosition - start;
  const startTime = performance.now();

  function scrollAnimation(currentTime) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);

    const ease = easeInOutQuad(progress);
    window.scrollTo(0, start + distance * ease);

    if (progress < 1) {
      requestAnimationFrame(scrollAnimation);
    }
  }

  function easeInOutQuad(t) {
    return t < 0.5
      ? 2 * t * t
      : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  requestAnimationFrame(scrollAnimation);
}